import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Send, Phone, Settings, Handshake, FileCheck } from 'lucide-react';

const ScrollingBannerFull = () => {
  const bannerItems = [
    "✔ 10+ Years of Experience",
    "✔ Faith-Driven Team",
    "✔ Broadcast-Level Equipment",
    "✔ Hands-On Technical Direction",
    "✔ On-Site or Remote Flexibility"
  ];

  return (
    <div className="overflow-hidden bg-obsidian-paper py-3 sm:py-4 border-y border-white/10">
      <motion.div
        className="flex whitespace-nowrap"
        animate={{
          x: [0, -1920],
        }}
        transition={{
          x: {
            repeat: Infinity,
            repeatType: "loop",
            duration: 40,
            ease: "linear",
          },
        }}
      >
        {[...bannerItems, ...bannerItems, ...bannerItems, ...bannerItems, ...bannerItems, ...bannerItems].map((item, index) => (
          <span key={index} className="mx-4 sm:mx-6 md:mx-8 text-sm sm:text-base md:text-lg lg:text-xl font-heading uppercase tracking-wider text-neon-blue">
            {item}
          </span>
        ))}
      </motion.div>
    </div>
  );
};

const Pricing = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.05 });

  const steps = [
    {
      id: "step-1",
      name: "Step 1 – Inquiry Submitted",
      icon: Send,
      description: "Schedule a Discovery Call",
      color: 'neon-blue'
    },
    {
      id: "step-2",
      name: "Step 2 – Discovery Call",
      icon: Phone,
      description: "Diagnose & Identify",
      color: 'neon-blue'
    },
    {
      id: "step-3",
      name: "Step 3 – Internal Strategy",
      icon: Settings,
      description: "We build a custom production pathway and prepare proposal link (24–48 Hours)",
      color: 'neon-pink'
    },
    {
      id: "step-4",
      name: "Step 4 – Strategy / Proposal",
      icon: Handshake,
      description: "Align & Resolve",
      color: 'neon-blue'
    },
    {
      id: "step-5",
      name: "Step 5 – Onboarding",
      icon: FileCheck,
      description: "Finalize agreement and begin your production journey.",
      color: 'neon-blue'
    }
  ];

  const getColorClass = (color, type) => {
    const colors = {
      'neon-blue': type === 'text' ? 'text-neon-blue' : type === 'border' ? 'border-neon-blue' : 'bg-neon-blue',
      'neon-pink': type === 'text' ? 'text-neon-pink' : type === 'border' ? 'border-neon-pink' : 'bg-neon-pink',
      'neon-yellow': type === 'text' ? 'text-neon-yellow' : type === 'border' ? 'border-neon-yellow' : 'bg-neon-yellow',
      'neon-orange': type === 'text' ? 'text-neon-orange' : type === 'border' ? 'border-neon-orange' : 'bg-neon-orange',
    };
    return colors[color] || colors['neon-blue'];
  };

  return (
    <section id="pricing" className="relative py-16 sm:py-20 md:py-24 lg:py-32 bg-obsidian-paper" data-testid="pricing-section" ref={ref}>
      {/* Background decoration - Blue line at top */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-neon-blue/50 to-transparent" />
      
      {/* Glow orbs */}
      <div className="glow-orb glow-orb-pink w-32 sm:w-48 md:w-64 h-32 sm:h-48 md:h-64 top-1/4 -right-16 sm:-right-24 md:-right-32 opacity-20" />
      <div className="glow-orb glow-orb-blue w-24 sm:w-36 md:w-48 h-24 sm:h-36 md:h-48 bottom-1/4 -left-12 sm:-left-18 md:-left-24 opacity-20" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-10 sm:mb-12 md:mb-16"
        >
          <span className="font-mono text-sm sm:text-base uppercase tracking-widest text-white mb-3 sm:mb-4 block">
            How It Works
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight mb-4 sm:mb-6" data-testid="pricing-heading">
            Our <span className="text-neon-pink">5-Step</span> Process
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-white/60">
            Every project is unique. Final pricing reflects your specific needs.
          </p>
        </motion.div>

        {/* Steps Cards - Responsive grid: 2 cols on mobile, 3 on tablet, 5 on desktop */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-10 sm:mb-12 md:mb-16">
          {steps.map((step, index) => (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`pricing-card relative px-3 sm:px-4 md:px-6 py-4 sm:py-5 text-center border ${getColorClass(step.color, 'border')} border-opacity-30 ${index === 4 ? 'col-span-2 sm:col-span-1' : ''}`}
              data-testid={`step-card-${step.id}`}
            >
              {/* Step Number */}
              <div className={`absolute -top-2 sm:-top-3 left-1/2 transform -translate-x-1/2 px-2 sm:px-3 md:px-4 py-0.5 sm:py-1 ${getColorClass(step.color, 'bg')} text-obsidian font-heading font-bold text-xs sm:text-sm`}>
                {index + 1}
              </div>

              {/* Icon */}
              <div className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 mx-auto mb-2 sm:mb-3 flex items-center justify-center border ${getColorClass(step.color, 'border')}`}>
                <step.icon className={`w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 ${getColorClass(step.color, 'text')}`} />
              </div>

              {/* Step name */}
              <h3 className={`font-heading text-[10px] sm:text-xs md:text-sm font-bold uppercase mb-1 sm:mb-2 ${getColorClass(step.color, 'text')}`}>
                {step.name}
              </h3>
              
              {/* Description */}
              <p className="text-[10px] sm:text-xs md:text-sm text-white font-medium leading-snug">
                {step.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Full Width Scrolling Banner */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8, delay: 0.5 }}
        className="mb-10 sm:mb-12 md:mb-16"
      >
        <ScrollingBannerFull />
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        {/* Ready To Elevate Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center"
        >
          <h3 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold uppercase text-white mb-6 sm:mb-8">
            Ready To <span className="text-neon-pink" style={{ textShadow: '0 0 20px rgba(255, 0, 153, 0.4)' }}>Elevate</span> Your Message?
          </h3>
          
          <a
            href="https://kingdomvibestudios.hbportal.co/public/69a9c1506b994e0034214eb6"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-neon btn-neon-pink inline-block text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4"
            data-testid="pricing-cta"
          >
            <span>Schedule a Discovery Call</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;
